import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { 
    Play, Pause, Trash2, Send, Plus, CheckCircle, 
    AlertTriangle, Loader2, ExternalLink, RefreshCw,
    Edit3, Check, X
} from 'lucide-react';

const TikTokIcon = ({ size = 20, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3 15.28a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.71a8.21 8.21 0 0 0 4.91 1.62v-3.5a4.87 4.87 0 0 1-1-.14z"/>
    </svg>
);

const TikTokOfferBank = () => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [rawInput, setRawInput] = useState('');
    const [activeCount, setActiveCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [minimumRequired, setMinimumRequired] = useState(10);
    const [isAutopostReady, setIsAutopostReady] = useState(false);
    const [disparandoId, setDisparandoId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editPrice, setEditPrice] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    const handleStartEdit = (offer) => {
        setEditingId(offer.id);
        setEditName(offer.productName || '');
        setEditPrice(offer.price && offer.price !== "Consulte o link" && offer.price !== "Consulte" ? offer.price : '');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditName('');
        setEditPrice('');
    };

    const handleSaveEdit = async (offerId) => {
        if (!editName.trim()) {
            return toast.error("O título do produto não pode ficar vazio.");
        }
        try {
            setSavingEdit(true);
            const cleanPrice = editPrice.trim().replace('R$', '').trim();
            const res = await api.put(`/tiktok-offers/${offerId}`, {
                productName: editName.trim(),
                price: cleanPrice || "Consulte"
            });
            if (res.data && res.data.success) {
                setOffers(prev => prev.map(o => o.id === offerId ? res.data.offer : o));
                setEditingId(null);
                toast.success("Produto atualizado com sucesso!");
            }
        } catch (err) {
            console.error("Erro ao salvar produto:", err);
            toast.error("Erro ao salvar alterações.");
        } finally {
            setSavingEdit(false);
        }
    };

    const fetchOffers = async () => {
        try {
            setLoading(true);
            const res = await api.get('/tiktok-offers');
            if (res.data && res.data.success) {
                setOffers(res.data.offers || []);
                setActiveCount(res.data.activeCount || 0);
                setTotalCount(res.data.totalCount || 0);
                setMinimumRequired(res.data.minimumRequired || 10);
                setIsAutopostReady(Boolean(res.data.isAutopostReady));
            }
        } catch (err) {
            console.error("Erro ao carregar banco do TikTok:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOffers();
    }, []);

    const handleImport = async () => {
        if (!rawInput.trim()) {
            return toast.error("Por favor, cole ao menos um link do TikTok Shop!");
        }

        setImporting(true);
        const loadToast = toast.loading("Processando e buscando detalhes dos produtos...");

        try {
            const res = await api.post('/tiktok-offers/import', { rawInput });
            if (res.data && res.data.success) {
                toast.success(res.data.message || "Links importados com sucesso!", { id: loadToast });
                setRawInput('');
                fetchOffers();
            } else {
                throw new Error("Erro na importação.");
            }
        } catch (err) {
            console.error("Erro na importação do TikTok:", err);
            const msg = err.response?.data?.error || "Erro ao processar links.";
            toast.error(msg, { id: loadToast });
        } finally {
            setImporting(false);
        }
    };

    const handleToggle = async (offerId) => {
        try {
            const res = await api.patch(`/tiktok-offers/${offerId}/toggle`);
            if (res.data && res.data.success) {
                setOffers(prev => prev.map(o => o.id === offerId ? res.data.offer : o));
                setActiveCount(res.data.activeCount);
                setIsAutopostReady(res.data.isAutopostReady);
                toast.success(res.data.offer.isActive ? "Produto ativado no rodízio!" : "Produto pausado!");
            }
        } catch (err) {
            toast.error("Erro ao alterar status.");
        }
    };

    const handleDelete = async (offerId, productName) => {
        if (!window.confirm(`Tem certeza que deseja remover "${productName}" do banco?`)) {
            return;
        }

        try {
            const res = await api.delete(`/tiktok-offers/${offerId}`);
            if (res.data && res.data.success) {
                setOffers(prev => prev.filter(o => o.id !== offerId));
                setActiveCount(res.data.activeCount);
                setIsAutopostReady(res.data.isAutopostReady);
                toast.success("Produto removido com sucesso!");
            }
        } catch (err) {
            toast.error("Erro ao excluir oferta.");
        }
    };

    const handleDispararAgora = async (offerId) => {
        setDisparandoId(offerId);
        const loadToast = toast.loading("Disparando oferta para seus canais...");

        try {
            const res = await api.post(`/tiktok-offers/${offerId}/disparar`);
            if (res.data && res.data.success) {
                toast.success(res.data.message || "Oferta disparada com sucesso!", { id: loadToast });
                fetchOffers();
            }
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Erro no disparo.";
            toast.error(msg, { id: loadToast });
            fetchOffers(); // Atualiza caso tenha sido marcado como esgotado
        } finally {
            setDisparandoId(null);
        }
    };

    const progressPercent = Math.min(100, Math.round((activeCount / minimumRequired) * 100));

    return (
        <div style={{
            background: 'white',
            borderRadius: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            padding: '30px',
            marginTop: '40px',
            textAlign: 'left'
        }}>
            {/* CABEÇALHO */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                        backgroundColor: '#010101',
                        color: 'white',
                        padding: '12px',
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                    }}>
                        <TikTokIcon size={24} color="#fe2c55" />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h3 style={{ margin: 0, color: '#0f172a', fontSize: '20px', fontWeight: '800' }}>
                                Banco de Ofertas - TikTok Shop
                            </h3>
                            <span style={{
                                backgroundColor: isAutopostReady ? '#dcfce7' : '#fef3c7',
                                color: isAutopostReady ? '#15803d' : '#b45309',
                                fontSize: '11px',
                                fontWeight: '700',
                                padding: '3px 10px',
                                borderRadius: '20px',
                                border: `1px solid ${isAutopostReady ? '#bbf7d0' : '#fde68a'}`
                            }}>
                                {isAutopostReady ? '🟢 Autopost Ativo' : '🟡 Mínimo 10 Links'}
                            </span>
                        </div>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                            Cadastre links comissionados copiados do aplicativo TikTok para rodar no Autopost (Feed c/ trava de 7 dias, Stories livres e Grupos).
                        </p>
                    </div>
                </div>

                <button
                    onClick={fetchOffers}
                    disabled={loading}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #cbd5e1',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    Atualizar
                </button>
            </div>

            {/* BARRA DE PROGRESSO DO ESTOQUE */}
            <div style={{
                backgroundColor: isAutopostReady ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${isAutopostReady ? '#bbf7d0' : '#fef3c7'}`,
                padding: '16px 20px',
                borderRadius: '14px',
                marginBottom: '25px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: isAutopostReady ? '#166534' : '#92400e' }}>
                        {isAutopostReady 
                            ? `🎉 Estoque Suficiente (${activeCount} links ativos)! O TikTok Shop está rodando no Autopost.`
                            : `⚠️ Faltam ${minimumRequired - activeCount} links para ativar o Autopost automático do TikTok Shop.`
                        }
                    </span>
                    <span style={{ fontSize: '12px', fontWeight: '800', color: isAutopostReady ? '#15803d' : '#b45309' }}>
                        {activeCount}/{minimumRequired} links ({progressPercent}%)
                    </span>
                </div>
                <div style={{ width: '100%', height: '10px', backgroundColor: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
                    <div style={{
                        width: `${progressPercent}%`,
                        height: '100%',
                        backgroundColor: isAutopostReady ? '#22c55e' : '#f59e0b',
                        transition: 'width 0.4s ease'
                    }} />
                </div>
                <small style={{ color: '#64748b', fontSize: '11px', marginTop: '6px', display: 'block' }}>
                    💡 <strong>Regra Inteligente:</strong> No Feed do Instagram, cada produto tem trava de 7 dias sem repetição. Nos Stories e Grupos, o rodízio é dinâmico. Produtos esgotados são pausados automaticamente.
                </small>
            </div>

            {/* FORMULÁRIO DE IMPORTAÇÃO EM LOTE */}
            <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '14px',
                padding: '20px',
                marginBottom: '25px'
            }}>
                <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                    📥 Importar Links Comissionados (Em Lote ou Avulso)
                </label>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                    Abra o app do TikTok no celular, clique em <strong>Compartilhar ➔ Copiar Link</strong> nos produtos da sua vitrine e cole aqui. Pode colar vários links de uma vez (um por linha):
                </p>
                <p style={{ fontSize: '11px', color: '#0369a1', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', padding: '6px 10px', borderRadius: '8px', marginBottom: '12px' }}>
                    💡 <strong>Dica:</strong> Se quiser, você já pode digitar o preço ao lado do link separado por espaço (Ex: <code>https://vt.tiktok.com/... 149.90</code>), ou definir depois na tabela abaixo clicando em <strong>Definir Preço</strong>.
                </p>
                <textarea
                    rows={3}
                    value={rawInput}
                    onChange={(e) => setRawInput(e.target.value)}
                    placeholder="https://vt.tiktok.com/ZS9yCbQXQRW7n-VGYpW/&#10;https://vt.tiktok.com/ZS9yCEXancLGF-XEvda/"
                    style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: '10px',
                        border: '1.5px solid #cbd5e1',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        outline: 'none',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                    }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <button
                        onClick={handleImport}
                        disabled={importing || !rawInput.trim()}
                        style={{
                            backgroundColor: importing || !rawInput.trim() ? '#94a3b8' : '#fe2c55',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '10px',
                            fontWeight: '700',
                            fontSize: '13px',
                            cursor: importing || !rawInput.trim() ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: '0.2s',
                            boxShadow: '0 4px 10px rgba(254, 44, 85, 0.25)'
                        }}
                    >
                        {importing ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                        {importing ? 'Processando e Buscando Detalhes...' : 'Cadastrar Links no Banco'}
                    </button>
                </div>
            </div>

            {/* LISTAGEM DE PRODUTOS CADASTRADOS */}
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0, color: '#1e293b', fontSize: '16px', fontWeight: '800' }}>
                        📦 Ofertas Cadastradas ({totalCount} no banco • {activeCount} ativas)
                    </h4>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        <Loader2 size={28} className="animate-spin" style={{ margin: '0 auto 10px auto' }} />
                        <p style={{ margin: 0, fontSize: '13px' }}>Carregando suas ofertas do TikTok...</p>
                    </div>
                ) : offers.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        border: '1.5px dashed #cbd5e1',
                        color: '#64748b'
                    }}>
                        <TikTokIcon size={32} color="#94a3b8" />
                        <p style={{ margin: '10px 0 0 0', fontWeight: '700', fontSize: '14px', color: '#475569' }}>
                            Nenhum produto cadastrado no banco ainda.
                        </p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                            Copie links de produtos no seu app do TikTok e cole no campo acima para começar!
                        </p>
                    </div>
                ) : (
                    <div style={{
                        overflowX: 'auto',
                        border: '1px solid #e2e8f0',
                        borderRadius: '14px',
                        backgroundColor: 'white'
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '700' }}>Produto</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '700' }}>Preço</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '700' }}>Status</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '700' }}>Disparos</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '700' }}>Último Envio</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '700', textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {offers.map((offer) => {
                                    const isOutOfStock = offer.status === 'out_of_stock';
                                    const isEditing = editingId === offer.id;
                                    return (
                                        <tr key={offer.id} style={{
                                            borderBottom: '1px solid #f1f5f9',
                                            backgroundColor: isEditing ? '#fff1f2' : (!offer.isActive ? '#fafafa' : 'white'),
                                            opacity: !offer.isActive && !isEditing ? 0.75 : 1,
                                            transition: '0.2s'
                                        }}>
                                            {/* FOTO E TÍTULO */}
                                            <td style={{ padding: '12px 16px', minWidth: '260px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    {offer.image ? (
                                                        <img
                                                            src={offer.image}
                                                            alt={offer.productName}
                                                            style={{ width: '42px', height: '42px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e2e8f0', flexShrink: 0 }}
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <div style={{ width: '42px', height: '42px', borderRadius: '8px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <TikTokIcon size={18} color="#94a3b8" />
                                                        </div>
                                                    )}
                                                    <div style={{ width: '100%', maxWidth: '320px' }}>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={editName}
                                                                onChange={(e) => setEditName(e.target.value)}
                                                                placeholder="Título do Produto..."
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '6px 10px',
                                                                    borderRadius: '6px',
                                                                    border: '1.5px solid #fe2c55',
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                    outline: 'none',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <div style={{ fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {offer.productName}
                                                            </div>
                                                        )}
                                                        <a
                                                            href={offer.offerLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ fontSize: '11px', color: '#fe2c55', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}
                                                        >
                                                            Link Oficial <ExternalLink size={10} />
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* PREÇO */}
                                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>R$</span>
                                                        <input
                                                            type="text"
                                                            value={editPrice}
                                                            onChange={(e) => setEditPrice(e.target.value)}
                                                            placeholder="0,00"
                                                            style={{
                                                                width: '80px',
                                                                padding: '6px 8px',
                                                                borderRadius: '6px',
                                                                border: '1.5px solid #fe2c55',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                outline: 'none'
                                                            }}
                                                        />
                                                    </div>
                                                ) : offer.price && offer.price !== "Consulte o link" && offer.price !== "Consulte" ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ fontWeight: '800', color: '#0f172a' }}>
                                                            R$ {offer.price}
                                                        </span>
                                                        <button
                                                            onClick={() => handleStartEdit(offer)}
                                                            title="Alterar Preço"
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: '#94a3b8',
                                                                cursor: 'pointer',
                                                                padding: '2px',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Edit3 size={11} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleStartEdit(offer)}
                                                        title="Clique para definir o preço deste produto"
                                                        style={{
                                                            backgroundColor: '#fef3c7',
                                                            border: '1px solid #fde047',
                                                            color: '#b45309',
                                                            padding: '4px 10px',
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: '700',
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            transition: '0.2s'
                                                        }}
                                                    >
                                                        <Edit3 size={12} />
                                                        Definir Preço
                                                    </button>
                                                )}
                                            </td>

                                            {/* STATUS */}
                                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                {isOutOfStock ? (
                                                    <span style={{
                                                        backgroundColor: '#fef3c7',
                                                        color: '#b45309',
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: '700',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <AlertTriangle size={12} /> Esgotado (Auto-Pausa)
                                                    </span>
                                                ) : offer.isActive ? (
                                                    <span style={{
                                                        backgroundColor: '#dcfce7',
                                                        color: '#15803d',
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: '700',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <CheckCircle size={12} /> Ativo no Rodízio
                                                    </span>
                                                ) : (
                                                    <span style={{
                                                        backgroundColor: '#f1f5f9',
                                                        color: '#64748b',
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: '700',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <Pause size={12} /> Pausado
                                                    </span>
                                                )}
                                            </td>

                                            {/* DISPAROS */}
                                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', color: '#475569', fontSize: '12px' }}>
                                                <div><strong>Total:</strong> {offer.timesPostedTotal || 0}x</div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                    Feed: {offer.timesPostedFeed || 0} | Stories: {offer.timesPostedStory || 0}
                                                </div>
                                            </td>

                                            {/* ÚLTIMO ENVIO */}
                                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', color: '#64748b', fontSize: '12px' }}>
                                                {offer.lastPostedAt ? (
                                                    new Date(offer.lastPostedAt).toLocaleDateString('pt-BR', {
                                                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                                    })
                                                ) : (
                                                    <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Ainda não enviado</span>
                                                )}
                                            </td>

                                            {/* AÇÕES */}
                                            <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleSaveEdit(offer.id)}
                                                                disabled={savingEdit}
                                                                title="Salvar alterações"
                                                                style={{
                                                                    backgroundColor: '#16a34a',
                                                                    border: 'none',
                                                                    color: 'white',
                                                                    padding: '6px 12px',
                                                                    borderRadius: '8px',
                                                                    cursor: savingEdit ? 'not-allowed' : 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '700'
                                                                }}
                                                            >
                                                                {savingEdit ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                                                Salvar
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                disabled={savingEdit}
                                                                title="Cancelar edição"
                                                                style={{
                                                                    backgroundColor: '#f1f5f9',
                                                                    border: '1px solid #cbd5e1',
                                                                    color: '#475569',
                                                                    padding: '6px 10px',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '600'
                                                                }}
                                                            >
                                                                <X size={12} />
                                                                Cancelar
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {/* BOTÃO DISPARAR AGORA */}
                                                            <button
                                                                onClick={() => handleDispararAgora(offer.id)}
                                                                disabled={disparandoId === offer.id}
                                                                title="Disparar Agora (Teste imediato)"
                                                                style={{
                                                                    backgroundColor: '#eff6ff',
                                                                    border: '1px solid #bfdbfe',
                                                                    color: '#2563eb',
                                                                    padding: '6px 10px',
                                                                    borderRadius: '8px',
                                                                    cursor: disparandoId === offer.id ? 'not-allowed' : 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '700'
                                                                }}
                                                            >
                                                                {disparandoId === offer.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                                                                Disparar
                                                            </button>

                                                            {/* EDITAR */}
                                                            <button
                                                                onClick={() => handleStartEdit(offer)}
                                                                title="Editar Título e Preço"
                                                                style={{
                                                                    backgroundColor: '#f8fafc',
                                                                    border: '1px solid #cbd5e1',
                                                                    color: '#334155',
                                                                    padding: '6px 10px',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '700'
                                                                }}
                                                            >
                                                                <Edit3 size={12} />
                                                                Editar
                                                            </button>

                                                            {/* TOGGLE ATIVAR / PAUSAR */}
                                                            <button
                                                                onClick={() => handleToggle(offer.id)}
                                                                title={offer.isActive ? "Pausar no Autopost" : "Ativar no Autopost"}
                                                                style={{
                                                                    backgroundColor: offer.isActive ? '#ecfdf5' : '#f8fafc',
                                                                    border: `1px solid ${offer.isActive ? '#a7f3d0' : '#cbd5e1'}`,
                                                                    color: offer.isActive ? '#059669' : '#64748b',
                                                                    padding: '6px 10px',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '700'
                                                                }}
                                                            >
                                                                {offer.isActive ? <Pause size={12} /> : <Play size={12} />}
                                                                {offer.isActive ? 'Pausar' : 'Ativar'}
                                                            </button>

                                                            {/* EXCLUIR */}
                                                            <button
                                                                onClick={() => handleDelete(offer.id, offer.productName)}
                                                                title="Excluir Oferta"
                                                                style={{
                                                                    backgroundColor: '#fff1f2',
                                                                    border: '1px solid #fecdd3',
                                                                    color: '#e11d48',
                                                                    padding: '6px 8px',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TikTokOfferBank;
